import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    PiCurrencyCircleDollar,
    PiEyeBold,
    PiEyeSlashBold,
    PiMinusCircle,
    PiPencilSimpleBold,
    PiPlusCircle,
    PiShieldCheckBold,
    PiSnowflakeBold,
    PiWarningBold,
    PiTableBold,
    PiToggleLeftBold,
    PiToggleRightFill,
    PiArrowsClockwiseBold,
    PiCalendarCheckBold,
    PiLockOpenBold,
    PiScalesBold,
    PiUserPlusBold,
    PiUserSwitchBold,
    PiTreeStructureBold,
    PiHeadsetBold,
    PiCaretDownBold,
    PiGearBold,
    PiProhibitBold,
    PiArrowUDownLeftBold,
    PiArrowsLeftRightBold,
    PiPauseCircleBold,
    PiCalendarPlusBold,
    PiCalendarDotsBold,
    PiWalletBold,
    PiWrenchBold,
} from 'react-icons/pi';
import toast from 'react-hot-toast';
import { Badge, Button, LoadingState, Text, Title } from '@v2/design-system';
import { cn } from '@v2/utils/cn';
import type { CommissionPreview } from '../types';
import DealOwnerCell from '../DealOwnerCell';
import { formatBrl } from '../utils';
import DetailCard from './DetailCard';
import ConfirmModal from '@v2/components/ui/ConfirmModal';
import V2Modal from '@v2/components/ui/V2Modal';
import DealCommissionEditModal, {
    AttachmentPreview,
    ChangeLogPanel,
    type EditHistoryItem,
} from './DealCommissionEditModal';
import DealCollaboratorLinkModal from './DealCollaboratorLinkModal';
import DealSdrLinkModal from './DealSdrLinkModal';
import DealSellerLinkModal from './DealSellerLinkModal';
import DealLegacyCascadeModal from './DealLegacyCascadeModal';
import DealScheduleRepairModal from './DealScheduleRepairModal';
import CommissionActionsModal, { type CommissionActionItem } from './CommissionActionsModal';
import CommissionAiAnalysisButton from '@v2/features/ai/CommissionAiAnalysisButton';
import DealCommissionActions, { DealActionBadges, type DealActionKey } from './DealCommissionActions';
import SaleCancellationCard from '@v2/pages/commissioning/SaleCancellationCard';
import { invalidateDealCommissionQueries } from './invalidateDealCommissionQueries';
import { COMMISSION_LIVE_QUERY } from '@v2/hooks/commissionLiveQuery';
import { canViewSellerBonus, hasRole, isSuperAdmin } from '@v2/lib/auth';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';

function formatPp(value: number | null | undefined): string {
    if (value === null || value === undefined || value === 0) {
        return '0 p.p.';
    }

    const abs = Math.abs(value);
    const formatted = new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: abs < 1 ? 1 : 0,
        maximumFractionDigits: 2,
    }).format(abs);

    return `${value < 0 ? '−' : '+'}${formatted} p.p.`;
}

function shouldShowCommissionSplit(preview: CommissionPreview): boolean {
    const split = preview.commission_split;
    if (!split?.allocations?.length) {
        return false;
    }

    const rule = String(split.rule ?? '');
    // Legado / DEFAULT com 1 pessoa não é rateio (falso positivo na #995).
    if (rule === 'legacy' || rule === 'DEFAULT' || rule === 'REG-DEFAULT') {
        return split.allocations.length > 1;
    }

    return split.allocations.length > 1;
}

function CommissionSplitCard({ preview }: { preview: CommissionPreview }) {
    const split = preview.commission_split;
    if (!split || !shouldShowCommissionSplit(preview)) {
        return null;
    }

    return (
        <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50/80 p-3 dark:border-zinc-700 dark:bg-zinc-800/60">
            <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-zinc-400">
                    Rateio de comissão
                </p>
                <Badge color="info" variant="flat" rounded="pill" size="sm">
                    {split.rule}
                </Badge>
            </div>
            <p className="text-xs text-gray-700 dark:text-zinc-300">{split.rule_label}</p>
            {!split.editable && (
                <p className="text-[11px] text-gray-500 dark:text-zinc-400">Percentuais fixos — não editáveis.</p>
            )}
            <ul className="space-y-2">
                {split.allocations.map((row) => (
                    <li
                        key={`${row.user?.id ?? row.user?.name ?? 'u'}-${row.participant_role ?? 'p'}`}
                        className="flex items-center justify-between gap-2 rounded-md bg-white/80 px-2.5 py-2 text-xs dark:bg-zinc-900/70"
                    >
                        <div className="flex min-w-0 items-center gap-2">
                            <DealOwnerCell name={row.user.name} avatarUrl={row.user.avatar_url} />
                            {row.participant_role && (
                                <Badge color="secondary" variant="flat" rounded="pill" size="sm">
                                    {row.participant_role}
                                </Badge>
                            )}
                        </div>
                        <div className="shrink-0 text-right">
                            <p className="font-semibold text-gray-900 dark:text-zinc-100">{formatBrl(row.amount)}</p>
                            <p className="text-[10px] text-gray-500 dark:text-zinc-400">
                                {row.percent.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%
                            </p>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

const ALCADA_LABELS: Record<string, string> = {
    seller: 'Vendedor',
    manager: 'Gerência',
    director: 'Diretoria',
};

type RevealLogEntry = {
    id: number;
    user_name: string;
    user_email: string;
    ip: string | null;
    revealed_at: string;
};

type Props = {
    dealId: number | string;
    /** Se vier embutido no deal (legado); senão busca lazy em /commission-preview. */
    initialPreview?: CommissionPreview | null;
};

export default function PloomesDealCommissionCard({ dealId, initialPreview = null }: Props) {
    const queryClient = useQueryClient();
    const { t } = useI18n();
    const canReveal = isSuperAdmin();
    const showSellerBonus = canViewSellerBonus();
    // Super admin começa oculto (precisa revelar). Demais perfis veem a comissão normalmente.
    const [revealed, setRevealed] = useState(!canReveal);
    const [showLog, setShowLog] = useState(false);
    const [showRulesModal, setShowRulesModal] = useState(false);
    const [showVersionModal, setShowVersionModal] = useState(false);
    const [legacyAction, setLegacyAction] = useState<'unfreeze' | 'refreeze' | 'realign' | null>(null);
    const [dealAction, setDealAction] = useState<DealActionKey | null>(null);
    const [actionsOpen, setActionsOpen] = useState(false);
    const [scheduleRepairOpen, setScheduleRepairOpen] = useState(false);
    const [saleCancelOpen, setSaleCancelOpen] = useState(false);
    const [legacyConfirm, setLegacyConfirm] = useState<'unfreeze' | 'refreeze' | 'realign' | null>(null);
    const [editCommissionOpen, setEditCommissionOpen] = useState(false);
    const [collaboratorLinkOpen, setCollaboratorLinkOpen] = useState(false);
    const [sdrLinkOpen, setSdrLinkOpen] = useState(false);
    const [sellerLinkOpen, setSellerLinkOpen] = useState(false);
    const [legacyCascadeOpen, setLegacyCascadeOpen] = useState(false);
    const [rateRealignOpen, setRateRealignOpen] = useState(false);
    const [rateTarget, setRateTarget] = useState('1.5');
    const [rateReason, setRateReason] = useState('');
    const [rateIncludeLocked, setRateIncludeLocked] = useState(true);
    const [sellerBonusOpen, setSellerBonusOpen] = useState(false);
    const [sellerBonusEnabled, setSellerBonusEnabled] = useState(true);
    const [sellerBonusReason, setSellerBonusReason] = useState('');
    const [historyAttachment, setHistoryAttachment] = useState<EditHistoryItem | null>(null);
    const previewQuery = useQuery<CommissionPreview | null>({
        queryKey: ['ploomes-deal-commission-preview', dealId],
        queryFn: async ({ signal }) => {
            const { data } = await api.get<{ data: CommissionPreview | null }>(
                `/ploomes-deals/${dealId}/commission-preview`,
                { signal, timeout: 60_000 },
            );
            return data.data;
        },
        enabled: Boolean(dealId),
        retry: 1,
        // Só primeira pintura — NÃO usar initialData (prende o cache e impede refresh SPA).
        placeholderData: initialPreview ?? undefined,
        ...COMMISSION_LIVE_QUERY,
        // Preview deve invalidar na hora após mutação local; polling cobre outro agente.
        staleTime: 0,
    });

    const preview = previewQuery.data ?? null;

    const logRevealMutation = useMutation({
        mutationFn: () => api.post(`/commissioning/deals/${dealId}/reveal-log`),
    });

    const invalidateDealCommission = async () => {
        await invalidateDealCommissionQueries(queryClient, dealId);
    };

    const rateSurfacesQuery = useQuery({
        queryKey: ['commission-rate-surfaces', dealId],
        queryFn: async () => {
            const { data } = await api.get<{ data: {
                card_rate_pp: number;
                schedule_rate_pp: number;
                report_rate_pp: number | null;
                suggested_rate_pp: number;
                locked_seller_slices: number;
                pending_seller_slices: number;
                divergent: boolean;
            } }>(`/commissioning/deals/${dealId}/rate-surfaces`);
            return data.data;
        },
        enabled: Boolean(dealId) && isSuperAdmin() && rateRealignOpen,
        staleTime: 15_000,
    });

    const rateRealignMutation = useMutation({
        mutationFn: (payload: { target_rate_pp: number; reason: string; include_locked: boolean }) =>
            api.post(`/commissioning/deals/${dealId}/realign-rates`, payload),
        onSuccess: async (res) => {
            toast.success(res.data?.message ?? t('v2.ploomes_deals.rate_realign.toast_ok', { rate: rateTarget }));
            setRateRealignOpen(false);
            setRateReason('');
            await invalidateDealCommission();
        },
        onError: (err: unknown) => {
            const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
                ?.response?.data;
            const first = data?.errors ? Object.values(data.errors).flat()[0] : null;
            toast.error(first || data?.message || t('v2.ploomes_deals.rate_realign.toast_error'));
        },
    });

    const sellerBonusMutation = useMutation({
        mutationFn: (payload: { enabled: boolean; reason: string }) =>
            api.post(`/commissioning/deals/${dealId}/seller-bonus`, payload),
        onSuccess: async (res) => {
            toast.success(
                res.data?.message
                ?? (sellerBonusEnabled
                    ? t('v2.ploomes_deals.seller_bonus.toast_enabled')
                    : t('v2.ploomes_deals.seller_bonus.toast_disabled')),
            );
            setSellerBonusOpen(false);
            setSellerBonusReason('');
            await invalidateDealCommission();
        },
        onError: (err: unknown) => {
            const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
                ?.response?.data;
            const first = data?.errors ? Object.values(data.errors).flat()[0] : null;
            toast.error(first || data?.message || t('v2.ploomes_deals.seller_bonus.toast_error'));
        },
    });

    const legacyRemediateMutation = useMutation({
        mutationFn: async (action: 'unfreeze' | 'refreeze' | 'realign') => {
            if (action === 'unfreeze') {
                return api.post(`/commissioning/legacy/deals/${dealId}/unfreeze`);
            }
            if (action === 'refreeze') {
                return api.post(`/commissioning/legacy/deals/${dealId}/refreeze`, {});
            }
            return api.post(`/commissioning/legacy/deals/${dealId}/realign-competence`);
        },
        onSuccess: async (_res, action) => {
            toast.success(
                action === 'unfreeze'
                    ? t('v2.ploomes_deals.legacy_remediate.toast_unfreeze')
                    : action === 'refreeze'
                      ? t('v2.ploomes_deals.legacy_remediate.toast_refreeze')
                      : t('v2.ploomes_deals.legacy_remediate.toast_realign'),
            );
            setLegacyAction(null);
            await invalidateDealCommission();
        },
        onError: (err: unknown) => {
            const status = (err as { response?: { status?: number; data?: { message?: string } } })?.response?.status;
            const apiMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
            if (status === 419) {
                toast.error(t('v2.ploomes_deals.legacy_remediate.toast_csrf'));
            } else if (status === 403) {
                toast.error(t('v2.ploomes_deals.legacy_remediate.toast_forbidden'));
            } else if (apiMessage) {
                toast.error(apiMessage);
            } else {
                toast.error(t('v2.ploomes_deals.legacy_remediate.toast_error'));
            }
            setLegacyAction(null);
        },
    });

    const materializeSlicesMutation = useMutation({
        mutationFn: () => api.post(`/commissioning/deals/${dealId}/materialize-slices`),
        onSuccess: async (res) => {
            toast.success(
                res.data?.message ?? t('v2.ploomes_deals.admin_actions.materialize_toast_ok'),
            );
            await invalidateDealCommission();
            await queryClient.invalidateQueries({ queryKey: ['commissioning', 'installments', dealId] });
        },
        onError: (err: unknown) => {
            const msg =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message
                ?? t('v2.ploomes_deals.admin_actions.materialize_toast_error');
            toast.error(msg);
        },
    });

    const revealLogQuery = useQuery<RevealLogEntry[]>({
        queryKey: ['commission-reveal-log', dealId],
        queryFn: () =>
            api
                .get(`/commissioning/deals/${dealId}/reveal-log`)
                .then((r) => r.data.data),
        enabled: showLog && canReveal,
        staleTime: 30_000,
    });

    const manualEditsQuery = useQuery({
        queryKey: ['commission-manual-edits', dealId],
        queryFn: async () => {
            const { data } = await api.get<{ data: EditHistoryItem[] }>(
                `/commissioning/deals/${dealId}/manual-edits`,
            );
            return data.data;
        },
        enabled: Boolean(dealId) && isSuperAdmin(),
        staleTime: 30_000,
    });

    function handleReveal() {
        if (!revealed) {
            logRevealMutation.mutate();
        }
        setRevealed((v) => !v);
    }

    if (previewQuery.isPending && !preview) {
        return (
            <DetailCard title="Comissão da Venda" icon={<PiCurrencyCircleDollar className="h-5 w-5" />}>
                <LoadingState variant="cards" />
            </DetailCard>
        );
    }

    if (previewQuery.isError && !preview) {
        return (
            <DetailCard title="Comissão da Venda" icon={<PiCurrencyCircleDollar className="h-5 w-5" />}>
                <p className="text-sm text-gray-500 dark:text-zinc-400">
                    {t('v2.ploomes_deals.commission_preview_error')}
                </p>
            </DetailCard>
        );
    }

    if (!preview) {
        return null;
    }

    if (!preview.available) {
        return (
            <DetailCard title="Comissão da Venda" icon={<PiCurrencyCircleDollar className="h-5 w-5" />}>
                <p className="text-sm text-gray-500 dark:text-zinc-400">
                    {preview.reason === 'no_amount'
                        ? 'Valor do deal indisponível para cálculo.'
                        : preview.reason === 'seller_not_found'
                          ? `Vendedor não encontrado no sistema${preview.owner_name ? ` (${preview.owner_name})` : ''}.`
                          : 'Cálculo indisponível.'}
                </p>
            </DetailCard>
        );
    }

    const isFixedTeam = preview.mode === 'fixed_team';
    const isFixedException = preview.mode === 'fixed_exception';
    const isFixedBucklerOne = preview.mode === 'fixed_buckler_one';
    const isLegacyFrozen = Boolean(preview.legacy_frozen) || preview.mode === 'legacy_frozen';
    const isFixedRateMode = isFixedException || isFixedBucklerOne;
    const seller = preview.seller;
    const exceptionRatePp =
        preview.commission_exception_rate != null
            ? preview.commission_exception_rate * 100
            : preview.base_rate_pp ?? preview.ploomes_exception_pct ?? null;

    const openRateRealign = () => {
        const suggested = rateSurfacesQuery.data?.suggested_rate_pp;
        setRateTarget(
            suggested != null
                ? String(suggested)
                : isFixedException
                  ? '2'
                  : isLegacyFrozen
                    ? '1.5'
                    : String(preview?.result_rate_pp ?? preview?.base_rate_pp ?? 2),
        );
        setRateReason('');
        setRateIncludeLocked(true);
        setRateRealignOpen(true);
    };

    const hasSellerBonus = (preview.bonus_rate_pp ?? 0) > 0.0001;
    const openSellerBonus = (enable: boolean) => {
        setSellerBonusEnabled(enable);
        setSellerBonusReason(
            enable
                ? 'Incluir adicional de 1,5% sem alterar a taxa base.'
                : 'Remover adicional de 1,5% sem alterar a taxa base.',
        );
        setSellerBonusOpen(true);
    };


    const headerActions = (
        <div className="flex items-center gap-1.5">
            {canReveal && (
                <>
                    <button
                        type="button"
                        onClick={() => setShowLog((v) => !v)}
                        title={t('v2.ploomes_deals.admin_actions.access_log')}
                        className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs font-medium text-gray-500 shadow-sm transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:border-blue-500/50 dark:hover:bg-blue-950/40 dark:hover:text-blue-300"
                    >
                        <PiShieldCheckBold className="h-3.5 w-3.5" />
                    </button>
                    <button
                        type="button"
                        onClick={handleReveal}
                        title={revealed
                            ? t('v2.ploomes_deals.admin_actions.hide_values')
                            : t('v2.ploomes_deals.admin_actions.reveal_values')}
                        className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-colors hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:border-orange-500/50 dark:hover:bg-orange-950/40 dark:hover:text-orange-300"
                    >
                        {revealed
                            ? <><PiEyeSlashBold className="h-3.5 w-3.5" /> {t('v2.ploomes_deals.admin_actions.hide_values')}</>
                            : <><PiEyeBold className="h-3.5 w-3.5" /> {t('v2.ploomes_deals.admin_actions.reveal_values')}</>}
                    </button>
                </>
            )}
            {preview.rule_version && (
                <button
                    type="button"
                    onClick={() => setShowVersionModal(true)}
                    title={[
                        t('v2.ploomes_deals.rules_modal_title', { version: preview.rule_version }),
                        preview.rule_version_meta?.is_current
                            ? t('v2.ploomes_deals.rules_status_current')
                            : t('v2.ploomes_deals.rules_status_legacy'),
                    ].join(' · ')}
                    className={cn(
                        'flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-colors',
                        'hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700',
                        'dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300',
                        'dark:hover:border-emerald-500/50 dark:hover:bg-emerald-950/40 dark:hover:text-emerald-300',
                        !preview.rule_version_meta?.is_current &&
                            'hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 dark:hover:border-amber-500/50 dark:hover:bg-amber-950/40 dark:hover:text-amber-300',
                    )}
                >
                    <span
                        className={cn(
                            'h-1.5 w-1.5 shrink-0 rounded-full',
                            preview.rule_version_meta?.is_current ? 'bg-emerald-500' : 'bg-amber-500',
                        )}
                        aria-hidden
                    />
                    {t('v2.ploomes_deals.rules_chip', { version: preview.rule_version })}
                    <span className="font-normal text-gray-400 dark:text-zinc-500">
                        {preview.rule_version_meta?.is_current
                            ? t('v2.ploomes_deals.rules_status_current_short')
                            : t('v2.ploomes_deals.rules_status_legacy_short')}
                    </span>
                </button>
            )}
            <CommissionAiAnalysisButton dealId={dealId} />
            <button
                onClick={() => setShowRulesModal(true)}
                title="Ver tabela de comissão por condição"
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:border-indigo-500/50 dark:hover:bg-indigo-950/40 dark:hover:text-indigo-300"
            >
                <PiTableBold className="h-3.5 w-3.5" />
                Tabela
            </button>
            {isSuperAdmin() && (
                <button
                    type="button"
                    title={t('v2.ploomes_deals.admin_actions.button')}
                    onClick={() => setActionsOpen(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 focus:outline-none dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:border-violet-500/50 dark:hover:bg-violet-950/40 dark:hover:text-violet-300"
                >
                    <PiGearBold className="h-3.5 w-3.5" />
                    {t('v2.ploomes_deals.admin_actions.button_short')}
                    <PiCaretDownBold className="h-3 w-3 opacity-70" />
                </button>
            )}
        </div>
    );

    return (
        <>
        {isSuperAdmin() && (
            <>
                <CommissionActionsModal
                    open={actionsOpen}
                    onClose={() => setActionsOpen(false)}
                    onHistory={() => setDealAction('history')}
                    items={[
                        { key: 'cancel', group: 'sale', icon: PiProhibitBold, tone: 'danger', title: t('v2.deal_actions.menu_cancel'), onRun: () => setSaleCancelOpen(true) },
                        { key: 'return', group: 'sale', icon: PiArrowUDownLeftBold, title: t('v2.deal_actions.menu_return'), onRun: () => setDealAction('return') },
                        { key: 'transfer', group: 'sale', icon: PiArrowsLeftRightBold, title: t('v2.deal_actions.menu_transfer'), onRun: () => setDealAction('transfer') },
                        { key: 'hold', group: 'sale', icon: PiPauseCircleBold, title: t('v2.deal_actions.menu_hold'), onRun: () => setDealAction('hold') },
                        { key: 'reschedule', group: 'installments', icon: PiCalendarPlusBold, title: t('v2.deal_actions.menu_reschedule'), onRun: () => setDealAction('reschedule') },
                        { key: 'repair', group: 'installments', icon: PiWrenchBold, title: t('v2.deal_actions.menu_repair'), onRun: () => setScheduleRepairOpen(true) },
                        { key: 'shift', group: 'installments', icon: PiCalendarDotsBold, title: t('v2.deal_actions.menu_shift'), onRun: () => setDealAction('shift') },
                        { key: 'wallet', group: 'wallet', icon: PiWalletBold, title: t('v2.deal_actions.menu_wallet'), onRun: () => setDealAction('wallet') },
                        ...((isLegacyFrozen || preview.available)
                            ? ([
                                { key: 'edit', group: 'adjust', icon: PiPencilSimpleBold, title: t('v2.ploomes_deals.commission_edit.button'), onRun: () => setEditCommissionOpen(true) },
                                { key: 'realign', group: 'adjust', icon: PiScalesBold, title: t('v2.ploomes_deals.rate_realign.button'), onRun: () => openRateRealign() },
                                {
                                    key: 'bonus',
                                    group: 'adjust',
                                    icon: hasSellerBonus ? PiMinusCircle : PiPlusCircle,
                                    title: hasSellerBonus ? t('v2.ploomes_deals.seller_bonus.disable') : t('v2.ploomes_deals.seller_bonus.enable'),
                                    disabled: sellerBonusMutation.isPending,
                                    onRun: () => openSellerBonus(!hasSellerBonus),
                                },
                            ] satisfies CommissionActionItem[])
                            : []),
                        { key: 'seller', group: 'rules', icon: PiUserSwitchBold, title: t('v2.ploomes_deals.seller_link.button_short'), onRun: () => setSellerLinkOpen(true) },
                        { key: 'ta', group: 'rules', icon: PiUserPlusBold, title: t('v2.ploomes_deals.collaborator_link.button_short'), onRun: () => setCollaboratorLinkOpen(true) },
                        { key: 'sdr', group: 'rules', icon: PiHeadsetBold, title: t('v2.ploomes_deals.sdr_link.button_short'), onRun: () => setSdrLinkOpen(true) },
                        { key: 'cascade', group: 'rules', icon: PiTreeStructureBold, title: t('v2.ploomes_deals.legacy_cascade.button_short'), onRun: () => setLegacyCascadeOpen(true) },
                        {
                            key: 'slices',
                            group: 'rules',
                            icon: PiArrowsClockwiseBold,
                            title: t('v2.ploomes_deals.admin_actions.materialize_slices'),
                            disabled: materializeSlicesMutation.isPending,
                            onRun: () => materializeSlicesMutation.mutate(),
                        },
                        ...(isLegacyFrozen
                            ? ([
                                { key: 'unfreeze', group: 'legacy', icon: PiLockOpenBold, title: t('v2.ploomes_deals.legacy_remediate.unfreeze'), disabled: legacyRemediateMutation.isPending, onRun: () => setLegacyConfirm('unfreeze') },
                                { key: 'refreeze', group: 'legacy', icon: PiArrowsClockwiseBold, title: t('v2.ploomes_deals.legacy_remediate.refreeze'), disabled: legacyRemediateMutation.isPending, onRun: () => setLegacyConfirm('refreeze') },
                                { key: 'realign_legacy', group: 'legacy', icon: PiCalendarCheckBold, title: t('v2.ploomes_deals.legacy_remediate.realign'), disabled: legacyRemediateMutation.isPending, onRun: () => setLegacyConfirm('realign') },
                            ] satisfies CommissionActionItem[])
                            : []),
                    ]}
                />
                <DealCommissionActions dealId={dealId} action={dealAction} onClose={() => setDealAction(null)} />
                <DealScheduleRepairModal dealId={dealId} open={scheduleRepairOpen} onClose={() => setScheduleRepairOpen(false)} />
                <SaleCancellationCard dealId={dealId} open={saleCancelOpen} onClose={() => setSaleCancelOpen(false)} hideTrigger />
            </>
        )}
        <DetailCard
            title={(
                <span className="flex flex-wrap items-center gap-2">
                    Comissão da Venda
                    <DealActionBadges dealId={dealId} enabled={isSuperAdmin()} />
                    {isLegacyFrozen && (
                        <span
                            className="inline-flex items-center gap-1 rounded-full bg-sky-500 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white dark:bg-sky-600"
                            title={t('v2.ploomes_deals.badges.commission_frozen_hint')}
                        >
                            <PiSnowflakeBold className="h-3 w-3" />
                            {t('v2.ploomes_deals.badges.commission_frozen')}
                        </span>
                    )}
                    {isFixedException && (
                        <span className="rounded-full bg-amber-500 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                            Exceção
                        </span>
                    )}
                    {isFixedBucklerOne && (
                        <span className="rounded-full bg-amber-600 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white dark:bg-amber-500">
                            Buckler One
                        </span>
                    )}
                    {preview.commission_floor && (
                        <span className="rounded-full bg-red-600 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white dark:bg-red-500">
                            Trava mínima {preview.minimum_rate_pp ?? 1}%
                        </span>
                    )}
                </span>
            )}
            icon={<PiCurrencyCircleDollar className="h-5 w-5" />}
            headerExtra={headerActions}
            headerStacked
            className={
                isLegacyFrozen
                    ? 'border-2 border-sky-300 bg-sky-50/25 dark:border-sky-700/50 dark:bg-sky-950/20'
                    : isFixedRateMode
                      ? 'border-2 border-amber-300 bg-amber-50/25 dark:border-amber-700/50 dark:bg-amber-950/20'
                      : undefined
            }
        >
            {isLegacyFrozen && (
                <div className="mb-4 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2.5 text-xs text-sky-950 dark:border-sky-800/50 dark:bg-sky-950/40 dark:text-sky-100">
                    <p className="flex items-center gap-1.5 font-semibold">
                        <PiSnowflakeBold className="h-3.5 w-3.5" />
                        {t('v2.ploomes_deals.badges.commission_frozen')}
                    </p>
                    <p className="mt-1 opacity-90">
                        {t('v2.ploomes_deals.badges.commission_frozen_hint')}
                    </p>
                    <p className="mt-1 opacity-80">
                        {t('v2.ploomes_deals.legacy_remediate.director_rate_hint')}
                    </p>
                    {isSuperAdmin() && (
                        <p className="mt-2 text-[11px] opacity-80">
                            {t('v2.ploomes_deals.admin_actions.legacy_banner_hint')}
                        </p>
                    )}
                    {isSuperAdmin() && (
                        <ChangeLogPanel
                            className="mt-3 border-sky-200 bg-white/70 dark:border-sky-800 dark:bg-zinc-950/30"
                            title={t('v2.ploomes_deals.commission_edit.history')}
                            loading={manualEditsQuery.isPending}
                            items={manualEditsQuery.data ?? []}
                            emptyLabel={t('v2.ploomes_deals.commission_edit.history_empty')}
                            loadingLabel={t('v2.ploomes_deals.commission_edit.history_loading')}
                            onOpenAttachment={(item) => setHistoryAttachment(item)}
                        />
                    )}
                </div>
            )}
            {!isLegacyFrozen && isSuperAdmin() && preview.available && (
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-950 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-100">
                    <p className="font-semibold">{t('v2.ploomes_deals.rate_realign.hub_title')}</p>
                    <p className="mt-1 opacity-90">{t('v2.ploomes_deals.rate_realign.hub_help')}</p>
                    <p className="mt-2 text-[11px] opacity-80">
                        {t('v2.ploomes_deals.admin_actions.hub_banner_hint')}
                    </p>
                </div>
            )}
            <div className={`space-y-4 transition-all duration-300 ${!revealed ? 'select-none blur-sm' : ''}`}>
                {isFixedException && (
                    <PloomesExceptionNotice ratePp={exceptionRatePp} />
                )}
                {isFixedBucklerOne && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-950 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-100">
                        <p className="font-semibold">Buckler One</p>
                        <p className="mt-1 opacity-90">
                            Comissão de {(preview.base_rate_pp ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}% da venda.
                            Ignora tabela, deflatores e exceção. Sem adicional 1,5%.
                            {preview.buckler_one_client_slug && (
                                <> Slug Cliente One: <span className="font-semibold">{preview.buckler_one_client_slug}</span>.</>
                            )}
                        </p>
                    </div>
                )}
                {preview.commission_floor && canReveal && (
                    <div className="rounded-xl border border-orange-200 bg-orange-50 px-3 py-3 text-xs text-orange-950 dark:border-orange-800/50 dark:bg-orange-950/30 dark:text-orange-100">
                        <p className="font-semibold">Trava mínima aplicada — comissão do vendedor</p>
                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                            <div className="rounded-lg border border-orange-100 bg-white/80 px-3 py-2 dark:border-orange-900/40 dark:bg-zinc-900/60">
                                <p className="text-[10px] font-medium uppercase tracking-wide text-orange-700/70 dark:text-orange-400/70">
                                    Antes da trava
                                </p>
                                <p className="mt-0.5 text-sm font-semibold text-gray-400 line-through dark:text-zinc-500">
                                    {preview.original_commission_amount != null
                                        ? formatBrl(preview.original_commission_amount)
                                        : '—'}
                                </p>
                                {preview.original_commission_rate != null && (
                                    <p className="text-[11px] text-gray-500 dark:text-zinc-400">
                                        {(preview.original_commission_rate * 100).toFixed(2)}% da venda
                                    </p>
                                )}
                            </div>
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-2 dark:border-emerald-800/50 dark:bg-emerald-950/40">
                                <p className="text-[10px] font-medium uppercase tracking-wide text-emerald-700/80 dark:text-emerald-400/80">
                                    Comissão devida do vendedor
                                </p>
                                <p className="mt-0.5 text-base font-bold text-emerald-700 dark:text-emerald-300">
                                    {formatBrl(preview.final_commission ?? preview.paid_commission_amount ?? 0)}
                                </p>
                                <p className="text-[11px] font-medium text-emerald-800 dark:text-emerald-400">
                                    {preview.minimum_rate_pp ?? 1}% mínimo desta venda
                                </p>
                            </div>
                        </div>
                        <p className="mt-2 opacity-90">
                            Hierarquia (gerente/diretor/sócio) zerada neste deal.
                            {preview.final_commission != null
                                && preview.original_commission_amount != null
                                && preview.final_commission > preview.original_commission_amount && (
                                <>
                                    {' '}Uplift da trava:{' '}
                                    <span className="font-semibold">
                                        {formatBrl(preview.final_commission - preview.original_commission_amount)}
                                    </span>
                                </>
                            )}
                        </p>
                    </div>
                )}
                {!canReveal && (preview.final_commission != null || preview.paid_commission_amount != null) && (
                    <SellerCommissionSummary preview={preview} />
                )}
                {canReveal && isFixedException && preview.exception_audit_warning && preview.exception_audit && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs text-rose-950 dark:border-rose-800/60 dark:bg-rose-950/40 dark:text-rose-100">
                        <p className="font-semibold flex items-center gap-1">
                            <PiWarningBold className="h-3.5 w-3.5" />
                            Auditoria de exceção
                        </p>
                        <p className="mt-0.5 opacity-90">
                            Regra Hub esperava {formatBrl(preview.exception_audit.expected_commission)}
                            {preview.exception_audit.would_hit_floor ? ' (com trava 1%)' : ''}; manual Ploomes{' '}
                            {formatBrl(preview.exception_audit.manual_commission)} (diferença{' '}
                            {formatBrl(preview.exception_audit.difference)}).
                        </p>
                    </div>
                )}
                <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    {seller && (
                        <div className="min-w-0 shrink">
                            <DealOwnerCell name={seller.name} avatarUrl={seller.avatar_url} />
                        </div>
                    )}
                    {(preview.final_commission != null || preview.deal_amount != null) && (
                        <div className="min-w-0 shrink-0 text-left sm:text-right">
                            <div className="flex flex-wrap items-start gap-3 sm:justify-end">
                                {preview.deal_amount != null && (
                                    <div>
                                        <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-zinc-500">
                                            {t('v2.ploomes_deals.commission_deal_amount')}
                                        </p>
                                        <p className="max-w-full break-words text-sm font-semibold tabular-nums text-gray-700 dark:text-zinc-200">
                                            {formatBrl(preview.deal_amount)}
                                        </p>
                                    </div>
                                )}
                                {preview.discount_pct != null && preview.discount_pct > 0 && (
                                    <div
                                        className={cn(
                                            'rounded-xl px-2.5 py-1.5 ring-1',
                                            preview.discount_pct > 30
                                                ? 'bg-rose-50 ring-rose-200 dark:bg-rose-950/40 dark:ring-rose-800/60'
                                                : preview.discount_pct > 20
                                                  ? 'bg-amber-50 ring-amber-200 dark:bg-amber-950/40 dark:ring-amber-800/60'
                                                  : 'bg-sky-50 ring-sky-200 dark:bg-sky-950/40 dark:ring-sky-800/60',
                                        )}
                                    >
                                        <p
                                            className={cn(
                                                'text-[10px] font-medium uppercase tracking-wide',
                                                preview.discount_pct > 30
                                                    ? 'text-rose-600 dark:text-rose-400'
                                                    : preview.discount_pct > 20
                                                      ? 'text-amber-700 dark:text-amber-400'
                                                      : 'text-sky-700 dark:text-sky-400',
                                            )}
                                        >
                                            {t('v2.ploomes_deals.commission_discount')}
                                        </p>
                                        <p
                                            className={cn(
                                                'text-sm font-bold tabular-nums',
                                                preview.discount_pct > 30
                                                    ? 'text-rose-700 dark:text-rose-300'
                                                    : preview.discount_pct > 20
                                                      ? 'text-amber-800 dark:text-amber-200'
                                                      : 'text-sky-800 dark:text-sky-200',
                                            )}
                                        >
                                            {preview.discount_pct.toLocaleString('pt-BR', {
                                                minimumFractionDigits: 1,
                                                maximumFractionDigits: 2,
                                            })}
                                            %
                                        </p>
                                    </div>
                                )}
                            </div>
                            {preview.final_commission != null && canReveal && !revealed && (
                                <div className="mt-1">
                                    <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-zinc-500">
                                        {preview.commission_floor ? 'Comissão do vendedor' : 'Comissão'}
                                    </p>
                                    <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{formatBrl(preview.final_commission)}</p>
                                    {preview.commission_floor && (
                                        <p className="text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
                                            {preview.minimum_rate_pp ?? 1}% (trava mínima)
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {canReveal && revealed && (preview.final_commission != null || preview.paid_commission_amount != null) && (
                    <SellerCommissionSummary preview={preview} rateLabel="Porcentagem" />
                )}

                {preview.team_name && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
                        <span>Squad: <span className="font-semibold text-gray-800 dark:text-zinc-200">{preview.team_name}</span></span>
                        {isFixedTeam && (
                            <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700 dark:bg-violet-950/50 dark:text-violet-300">
                                Fixo
                            </span>
                        )}
                    </div>
                )}

                {preview.payment_rule_missing && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-100">
                        <p className="font-semibold">Condição de pagamento não identificada</p>
                        <p className="mt-0.5 opacity-90">
                            Sem parcelas/entrada da cotação Ploomes a taxa base fica 0% (estrutura).
                            Sincronize o deal ou rode o backfill de cotação para calcular a comissão.
                        </p>
                    </div>
                )}

                {!isFixedTeam && preview.entry_split_count != null && Number(preview.entry_split_count) > 1 && (
                    <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 dark:border-amber-700/70 dark:bg-amber-950/40">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                            {t('v2.ploomes_deals.installments.entry_split_title')}
                        </p>
                        <p className="mt-0.5 text-sm font-semibold text-amber-950 dark:text-amber-50">
                            {t('v2.ploomes_deals.installments.entry_split_math', {
                                entry: preview.entry_split_count,
                                regular: preview.payment_parcelas ?? 0,
                                total: preview.payment_parcelas_total
                                    ?? Number(preview.payment_parcelas ?? 0) + Number(preview.entry_split_count),
                            })}
                        </p>
                        <p className="mt-0.5 text-[11px] text-amber-800 dark:text-amber-200">
                            {t('v2.ploomes_deals.installments.entry_split_band', {
                                total: preview.payment_parcelas_total
                                    ?? Number(preview.payment_parcelas ?? 0) + Number(preview.entry_split_count),
                            })}
                        </p>
                    </div>
                )}

                {!isFixedTeam && (
                    <div className="grid grid-cols-2 gap-2 rounded-lg border border-gray-100 bg-gray-50/80 p-3 text-xs dark:border-zinc-700 dark:bg-zinc-800/60 sm:grid-cols-4">
                        <div>
                            <p className="mb-0.5 font-medium uppercase tracking-wide text-gray-400 dark:text-zinc-500">Condição de pagamento</p>
                            <p className="font-semibold text-gray-900 dark:text-zinc-100">
                                {/* Em exceção/Buckler One, payment_label é o modo — condição real vem da cotação. */}
                                {isFixedRateMode
                                    ? (preview.payment_condition_text || '—')
                                    : (preview.payment_label || preview.payment_condition_text || '—')}
                            </p>
                            {(preview.payment_parcelas != null && Number(preview.payment_parcelas) > 0) ||
                            (preview.payment_parcelas_total != null && Number(preview.payment_parcelas_total) > 0) ? (
                                <p className="mt-0.5 text-[11px] leading-snug text-gray-500 dark:text-zinc-400">
                                    {preview.entry_split_count != null && Number(preview.entry_split_count) > 1
                                        ? t('v2.ploomes_deals.installments.entry_split_band', {
                                              total: preview.payment_parcelas_total
                                                  ?? Number(preview.payment_parcelas ?? 0) + Number(preview.entry_split_count),
                                          })
                                        : preview.payment_parcelas_total != null
                                        && preview.entry_parcelas != null
                                        && Number(preview.entry_parcelas) > 0
                                        && Number(preview.payment_parcelas_total) > Number(preview.payment_parcelas ?? 0)
                                        ? t('v2.ploomes_deals.commission_parcelas_hint', {
                                              total: preview.payment_parcelas_total,
                                              entry: preview.entry_parcelas,
                                              regular: preview.payment_parcelas ?? 0,
                                          })
                                        : t('v2.ploomes_deals.commission_parcelas_regular', {
                                              n: preview.payment_parcelas ?? preview.payment_parcelas_total ?? 0,
                                          })}
                                </p>
                            ) : null}
                        </div>
                        <div>
                            <p className="mb-0.5 font-medium uppercase tracking-wide text-gray-400 dark:text-zinc-500">Forma de pagamento</p>
                            <p className="font-semibold text-gray-900 dark:text-zinc-100">
                                {preview.payment_form?.trim() ? preview.payment_form : '—'}
                            </p>
                        </div>
                        <div>
                            <p className="mb-0.5 font-medium uppercase tracking-wide text-gray-400 dark:text-zinc-500">Entrada</p>
                            {preview.entry_pct != null && preview.entry_pct > 0 ? (
                                <>
                                    <p className="font-semibold text-gray-900 dark:text-zinc-100">
                                        {preview.entry_pct.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%
                                    </p>
                                    {preview.entry_amount != null && (
                                        <p className="text-[11px] text-gray-500 dark:text-zinc-400">{formatBrl(preview.entry_amount)}</p>
                                    )}
                                    {preview.entry_split_count != null && Number(preview.entry_split_count) > 1 && (
                                        <p className="mt-0.5 text-[11px] font-semibold leading-snug text-amber-800 dark:text-amber-200">
                                            {t('v2.ploomes_deals.installments.entry_split', {
                                                n: preview.entry_split_count,
                                            })}
                                        </p>
                                    )}
                                </>
                            ) : (
                                <p className="font-semibold text-gray-400 dark:text-zinc-500">—</p>
                            )}
                        </div>
                        <div>
                            <p className="mb-0.5 font-medium uppercase tracking-wide text-gray-400 dark:text-zinc-500">
                                {t('v2.ploomes_deals.commission_discount')}
                            </p>
                            {preview.discount_pct != null && preview.discount_pct > 0 ? (
                                <p
                                    className={cn(
                                        'font-bold tabular-nums',
                                        preview.discount_pct > 30
                                            ? 'text-rose-600 dark:text-rose-400'
                                            : preview.discount_pct > 20
                                              ? 'text-amber-700 dark:text-amber-300'
                                              : 'text-gray-900 dark:text-zinc-100',
                                    )}
                                >
                                    {preview.discount_pct.toLocaleString('pt-BR', {
                                        minimumFractionDigits: 1,
                                        maximumFractionDigits: 2,
                                    })}
                                    %
                                </p>
                            ) : (
                                <p className="font-semibold text-gray-400 dark:text-zinc-500">—</p>
                            )}
                        </div>
                    </div>
                )}

                {isFixedTeam ? (
                    <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-3 dark:border-zinc-700 dark:bg-zinc-800/60">
                        <p className="text-xs text-gray-500 dark:text-zinc-400">Taxa fixa do vendedor</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
                            {preview.fixed_rate_pp != null ? `${preview.fixed_rate_pp.toLocaleString('pt-BR')}%` : '—'}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-2">
                            <RateChip label="Base" value={preview.base_rate_pp} tone="neutral" />
                            {showSellerBonus
                                && !preview.commission_exception
                                && preview.bonus_rate_pp != null
                                && preview.bonus_rate_pp > 0 && (
                                <RateChip label="Adicional" value={preview.bonus_rate_pp} tone="positive" prefix="+" />
                            )}
                            <RateChip
                                label="Deflator"
                                value={preview.deflator_pp}
                                tone="negative"
                                prefix="−"
                                icon={<PiMinusCircle className="h-3.5 w-3.5" />}
                            />
                            <RateChip
                                label="Acelerador"
                                value={preview.accelerator_pp}
                                tone="positive"
                                prefix="+"
                                icon={<PiPlusCircle className="h-3.5 w-3.5" />}
                            />
                        </div>

                        {(() => {
                            const totalPp = resolveSellerCommissionRatePp(preview, showSellerBonus);
                            if (totalPp == null) return null;
                            return (
                                <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-300 bg-white px-3 py-2.5 dark:border-emerald-800/60 dark:bg-zinc-900">
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                                            Porcentagem total
                                        </p>
                                        <p className="text-[11px] text-gray-500 dark:text-zinc-400">
                                            {showSellerBonus
                                                ? 'Base − deflator + acelerador + adicional'
                                                : 'Base − deflator + acelerador'}
                                        </p>
                                    </div>
                                    <p className="text-xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                                        {formatRatePp(totalPp)}
                                    </p>
                                </div>
                            );
                        })()}

                        {(
                            preview.alcada_level
                            || (preview.commission_exception && !isFixedException)
                            || (preview.base_detail != null && preview.base_detail.discount_pct > preview.base_detail.seller_teto_pct)
                            || (showSellerBonus && (preview.bonus_items?.length ?? 0) > 0)
                            || (preview.deflators?.length ?? 0) > 0
                            || (preview.accelerators?.length ?? 0) > 0
                        ) && (
                            <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50/60 px-3 py-3 dark:border-zinc-700 dark:bg-zinc-900/40">
                                {(preview.alcada_level || (preview.commission_exception && !isFixedException)) && (
                                    <div className="flex flex-wrap gap-2 text-xs">
                                        {preview.alcada_level && (
                                            <span className="rounded-full bg-amber-50 px-2.5 py-1 font-medium text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">
                                                Alçada: {ALCADA_LABELS[preview.alcada_level] ?? preview.alcada_level}
                                            </span>
                                        )}
                                        {preview.commission_exception && !isFixedException && (
                                            <span className="rounded-full bg-rose-50 px-2.5 py-1 font-medium text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">
                                                Sem adicional
                                            </span>
                                        )}
                                    </div>
                                )}

                                {(
                                    (preview.base_detail != null && preview.base_detail.discount_pct > preview.base_detail.seller_teto_pct)
                                    || (showSellerBonus && (preview.bonus_items?.length ?? 0) > 0)
                                    || (preview.deflators?.length ?? 0) > 0
                                    || (preview.accelerators?.length ?? 0) > 0
                                ) && (
                                    <div className={cn(
                                        'space-y-2.5',
                                        (preview.alcada_level || (preview.commission_exception && !isFixedException))
                                            && 'border-t border-gray-200/80 pt-3 dark:border-zinc-700',
                                    )}>
                                        {preview.base_detail && (preview.base_detail.discount_pct > preview.base_detail.seller_teto_pct) && (
                                            <BaseDetailRow detail={preview.base_detail} />
                                        )}
                                        {preview.deflators?.map((item) => (
                                            <ModifierRow key={item.name} item={item} tone="negative" />
                                        ))}
                                        {preview.accelerators?.map((item) => (
                                            <ModifierRow key={item.name} item={item} tone="positive" />
                                        ))}
                                        {showSellerBonus && preview.bonus_items?.map((item) => (
                                            <ModifierRow key={item.name} item={item} tone="positive" />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {(preview.base_commission != null || (showSellerBonus && preview.bonus_commission != null)) && (
                            <div className="flex flex-wrap justify-between gap-2 rounded-xl border border-gray-100 bg-white px-3 py-3 text-xs text-gray-500 dark:border-zinc-800 dark:bg-zinc-900/30 dark:text-zinc-400">
                                {preview.base_commission != null && (
                                    <span>
                                        Base: <strong className="text-gray-800 dark:text-zinc-200">{formatBrl(preview.base_commission)}</strong>
                                    </span>
                                )}
                                {showSellerBonus && preview.bonus_commission != null && preview.bonus_commission > 0 && (
                                    <span>
                                        Adicional: <strong className="text-gray-800 dark:text-zinc-200">{formatBrl(preview.bonus_commission)}</strong>
                                    </span>
                                )}
                            </div>
                        )}

                        {/* SDR: % da comissão do Owner quando SDR Ploomes ≠ OwnerId */}
                        {preview.sdr_preview && preview.sdr_preview.amount > 0 && (
                            <div className="overflow-hidden rounded-xl border border-teal-100 bg-gradient-to-br from-teal-50/70 to-white dark:border-teal-900/40 dark:from-teal-950/40 dark:to-zinc-900">
                                <div className="flex items-center gap-2 border-b border-teal-100 px-3 py-2 dark:border-teal-900/40">
                                    <span className="h-1.5 w-1.5 rounded-full bg-teal-400" />
                                    <p className="text-[11px] font-semibold uppercase tracking-widest text-teal-600 dark:text-teal-400">
                                        SDR
                                    </p>
                                    <span className="ml-auto text-[10px] text-teal-400">
                                        {preview.sdr_preview.share_rate_pp}% da base · abatido do owner
                                    </span>
                                </div>
                                <div className="flex items-center gap-3 px-3 py-2.5">
                                    {preview.sdr_preview.user.avatar_url ? (
                                        <img
                                            src={preview.sdr_preview.user.avatar_url}
                                            alt={preview.sdr_preview.user.name}
                                            className="h-8 w-8 shrink-0 rounded-full object-cover shadow-sm ring-2 ring-white dark:ring-zinc-900"
                                        />
                                    ) : (
                                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-100 text-[11px] font-bold text-teal-700 shadow-sm ring-2 ring-white dark:bg-teal-950/50 dark:text-teal-300 dark:ring-zinc-900">
                                            {preview.sdr_preview.user.name.charAt(0)}
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-xs font-semibold text-gray-800 leading-tight dark:text-zinc-100">
                                            {preview.sdr_preview.user.name.split(' ').slice(0, 2).join(' ')}
                                        </p>
                                        <p className="text-[10px] text-teal-500 leading-tight">
                                            {preview.sdr_preview.share_rate_pp}% sobre{' '}
                                            {formatBrl(preview.sdr_preview.base_amount)}
                                            {showSellerBonus ? ' (sem o adicional 1,5%)' : ''}
                                        </p>
                                    </div>
                                    <div className="shrink-0 text-right">
                                        <p className="text-sm font-bold text-teal-700 leading-tight dark:text-teal-300">
                                            {formatBrl(preview.sdr_preview.amount)}
                                        </p>
                                        <p className="text-[9px] font-medium uppercase tracking-wide text-rose-500">
                                            − do owner
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <CommissionSplitCard preview={preview} />

                        {/* Cascade: gerente/diretor sobre este deal */}
                        {preview.cascade_preview && preview.cascade_preview.length > 0 && (
                            <div className="overflow-hidden rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/70 to-white dark:border-indigo-900/40 dark:from-indigo-950/40 dark:to-zinc-900">
                                <div className="flex items-center gap-2 border-b border-indigo-100 px-3 py-2 dark:border-indigo-900/40">
                                    <span className="h-1.5 w-1.5 rounded-full bg-indigo-400" />
                                    <p className="text-[11px] font-semibold uppercase tracking-widest text-indigo-500 dark:text-indigo-400">
                                        Liderança
                                    </p>
                                    <span className="ml-auto text-[10px] text-indigo-300 dark:text-indigo-500/80">valor total previsto</span>
                                </div>
                                <ul className="divide-y divide-indigo-50 dark:divide-indigo-900/30">
                                    {preview.cascade_preview.map((c) => {
                                        const roleLabel: Record<string, string> = {
                                            gerente: 'Gerente',
                                            diretor: 'Diretor',
                                            socio_diretor: 'Sócio-Dir.',
                                            lideranca: 'Liderança',
                                        };
                                        return (
                                            <li key={c.beneficiary_id} className="flex items-center gap-3 px-3 py-2.5">
                                                {/* Avatar */}
                                                {c.beneficiary_avatar ? (
                                                    <img
                                                        src={c.beneficiary_avatar}
                                                        alt={c.beneficiary_name}
                                                        className="h-8 w-8 shrink-0 rounded-full object-cover shadow-sm ring-2 ring-white dark:ring-zinc-900"
                                                    />
                                                ) : (
                                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[11px] font-bold text-indigo-600 shadow-sm ring-2 ring-white dark:bg-indigo-950/50 dark:text-indigo-300 dark:ring-zinc-900">
                                                        {c.beneficiary_name.charAt(0)}
                                                    </div>
                                                )}

                                                {/* Info */}
                                                <div className="min-w-0 flex-1">
                                                    <p className="truncate text-xs font-semibold leading-tight text-gray-800 dark:text-zinc-100">
                                                        {c.beneficiary_name.split(' ').slice(0, 2).join(' ')}
                                                    </p>
                                                    <p className="text-[10px] leading-tight text-indigo-400 dark:text-indigo-400/90">
                                                        {roleLabel[c.commission_role] ?? c.commission_role}
                                                        {' · '}
                                                        {c.rate_pp}%{' '}
                                                        {c.base_type === 'seller_commission' ? 'da comissão' : 'da venda'}
                                                    </p>
                                                </div>

                                                {/* Amount */}
                                                <div className="shrink-0 text-right">
                                                    <p className="text-sm font-bold leading-tight text-indigo-600 dark:text-indigo-300">
                                                        {formatBrl(c.amount)}
                                                    </p>
                                                    <p className="text-[10px] leading-tight text-gray-400 dark:text-zinc-500">valor total</p>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </>
                )}

                {isFixedTeam && <CommissionSplitCard preview={preview} />}
            </div>

            {/* Histórico de acessos — visível só para super_admin após clicar no escudo */}
            {canReveal && showLog && (
                <div className="mt-4 border-t border-gray-100 pt-3">
                    <p className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <PiShieldCheckBold className="h-3.5 w-3.5 text-blue-500" />
                        Histórico de acessos a comissão
                    </p>
                    {revealLogQuery.isLoading && (
                        <p className="text-xs text-gray-400">Carregando…</p>
                    )}
                    {revealLogQuery.data?.length === 0 && (
                        <p className="text-xs text-gray-400">Nenhum acesso registrado.</p>
                    )}
                    {(revealLogQuery.data ?? []).length > 0 && (
                        <ul className="space-y-1.5">
                            {(revealLogQuery.data ?? []).map((entry) => (
                                <li key={entry.id} className="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-2.5 py-1.5 text-xs">
                                    <span className="min-w-0 truncate font-medium text-gray-700">
                                        {entry.user_name}
                                        {entry.ip && (
                                            <span className="ml-1.5 font-normal text-gray-400">{entry.ip}</span>
                                        )}
                                    </span>
                                    <span className="shrink-0 text-gray-400">{entry.revealed_at}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </DetailCard>

        {/* Modal da tabela de regras de comissão */}
        {showRulesModal && (
            <PaymentRulesModal
                paymentCondition={preview.payment_condition ?? null}
                paymentLabel={preview.payment_label ?? preview.payment_condition_text ?? null}
                paymentParcelas={preview.payment_parcelas ?? null}
                discountPct={preview.discount_pct ?? null}
                onClose={() => setShowRulesModal(false)}
            />
        )}

        <FrozenRulesModal
            open={showVersionModal && Boolean(preview.rule_version)}
            version={preview.rule_version ?? ''}
            meta={preview.rule_version_meta ?? null}
            snapshot={preview.rules_snapshot ?? null}
            appliedDeflators={preview.deflators ?? []}
            appliedAccelerators={preview.accelerators ?? []}
            carenciaApplied={(preview.carencia_deflator_pp ?? 0) > 0}
            onClose={() => setShowVersionModal(false)}
        />

        <ConfirmModal
            open={legacyConfirm !== null}
            onClose={() => setLegacyConfirm(null)}
            title={
                legacyConfirm === 'unfreeze'
                    ? t('v2.ploomes_deals.legacy_remediate.unfreeze')
                    : legacyConfirm === 'refreeze'
                      ? t('v2.ploomes_deals.legacy_remediate.refreeze')
                      : t('v2.ploomes_deals.legacy_remediate.realign')
            }
            description={
                legacyConfirm === 'unfreeze'
                    ? t('v2.ploomes_deals.legacy_remediate.confirm_unfreeze')
                    : legacyConfirm === 'refreeze'
                      ? t('v2.ploomes_deals.legacy_remediate.confirm_refreeze')
                      : t('v2.ploomes_deals.legacy_remediate.confirm_realign')
            }
            confirmLabel={t('v2.ploomes_deals.legacy_remediate.confirm_action')}
            cancelLabel={t('v2.ploomes_deals.legacy_remediate.cancel_action')}
            loading={legacyRemediateMutation.isPending}
            onConfirm={() => {
                if (!legacyConfirm) return;
                const action = legacyConfirm;
                setLegacyConfirm(null);
                setLegacyAction(action);
                legacyRemediateMutation.mutate(action);
            }}
        />

        <ConfirmModal
            open={rateRealignOpen}
            onClose={() => {
                if (rateRealignMutation.isPending) return;
                setRateRealignOpen(false);
            }}
            title={t('v2.ploomes_deals.rate_realign.title')}
            description={t('v2.ploomes_deals.rate_realign.help')}
            confirmLabel={t('v2.ploomes_deals.rate_realign.confirm')}
            cancelLabel={t('v2.ploomes_deals.rate_realign.cancel')}
            loading={rateRealignMutation.isPending}
            onConfirm={() => {
                const rate = Number(String(rateTarget).replace(',', '.'));
                const reason = rateReason.trim();
                if (!Number.isFinite(rate) || rate <= 0) {
                    toast.error(t('v2.ploomes_deals.rate_realign.toast_error'));
                    return;
                }
                if (reason.length < 5) {
                    toast.error(t('v2.ploomes_deals.commission_edit.reason'));
                    return;
                }
                rateRealignMutation.mutate({
                    target_rate_pp: rate,
                    reason,
                    include_locked: rateIncludeLocked,
                });
            }}
        >
            <div className="space-y-3 text-sm">
                {rateSurfacesQuery.isLoading && <LoadingState variant="inline" />}
                {rateSurfacesQuery.data && (
                    <ul className="space-y-1 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs dark:border-amber-800/50 dark:bg-amber-950/30">
                        <li>
                            {t('v2.ploomes_deals.rate_realign.card')}:{' '}
                            <strong>{rateSurfacesQuery.data.card_rate_pp.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%</strong>
                        </li>
                        <li>
                            {t('v2.ploomes_deals.rate_realign.schedule')}:{' '}
                            <strong>{rateSurfacesQuery.data.schedule_rate_pp.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%</strong>
                        </li>
                        <li>
                            {t('v2.ploomes_deals.rate_realign.report')}:{' '}
                            <strong>
                                {rateSurfacesQuery.data.report_rate_pp != null
                                    ? `${rateSurfacesQuery.data.report_rate_pp.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`
                                    : '—'}
                            </strong>
                        </li>
                        {rateSurfacesQuery.data.locked_seller_slices > 0 && (
                            <li className="pt-1 text-amber-900 dark:text-amber-200">
                                {t('v2.ploomes_deals.rate_realign.locked_count', {
                                    count: rateSurfacesQuery.data.locked_seller_slices,
                                })}
                            </li>
                        )}
                    </ul>
                )}
                <label className="block space-y-1">
                    <span className="text-xs font-semibold text-gray-700 dark:text-zinc-200">
                        {t('v2.ploomes_deals.rate_realign.target')}
                    </span>
                    <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max="100"
                        value={rateTarget}
                        onChange={(e) => setRateTarget(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                    />
                    {rateSurfacesQuery.data?.suggested_rate_pp != null && (
                        <button
                            type="button"
                            className="text-[11px] text-sky-700 underline dark:text-sky-300"
                            onClick={() => setRateTarget(String(rateSurfacesQuery.data!.suggested_rate_pp))}
                        >
                            Usar sugerida ({rateSurfacesQuery.data.suggested_rate_pp}%)
                        </button>
                    )}
                </label>
                <label className="block space-y-1">
                    <span className="text-xs font-semibold text-gray-700 dark:text-zinc-200">
                        {t('v2.ploomes_deals.rate_realign.reason')}
                    </span>
                    <textarea
                        rows={3}
                        value={rateReason}
                        onChange={(e) => setRateReason(e.target.value)}
                        placeholder={t('v2.ploomes_deals.rate_realign.reason_ph')}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                    />
                </label>
                <label className="flex items-start gap-2 text-xs text-gray-700 dark:text-zinc-200">
                    <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={rateIncludeLocked}
                        onChange={(e) => setRateIncludeLocked(e.target.checked)}
                    />
                    <span>
                        <span className="font-semibold">{t('v2.ploomes_deals.rate_realign.include_locked')}</span>
                        <span className="mt-0.5 block opacity-80">
                            {t('v2.ploomes_deals.rate_realign.include_locked_hint')}
                        </span>
                    </span>
                </label>
            </div>
        </ConfirmModal>

        <ConfirmModal
            open={sellerBonusOpen}
            onClose={() => {
                if (sellerBonusMutation.isPending) return;
                setSellerBonusOpen(false);
            }}
            title={sellerBonusEnabled
                ? t('v2.ploomes_deals.seller_bonus.title_enable')
                : t('v2.ploomes_deals.seller_bonus.title_disable')}
            description={sellerBonusEnabled
                ? t('v2.ploomes_deals.seller_bonus.help_enable')
                : t('v2.ploomes_deals.seller_bonus.help_disable')}
            confirmLabel={sellerBonusEnabled
                ? t('v2.ploomes_deals.seller_bonus.confirm_enable')
                : t('v2.ploomes_deals.seller_bonus.confirm_disable')}
            cancelLabel={t('v2.ploomes_deals.seller_bonus.cancel')}
            loading={sellerBonusMutation.isPending}
            onConfirm={() => {
                const reason = sellerBonusReason.trim();
                if (reason.length < 5) {
                    toast.error(t('v2.ploomes_deals.commission_edit.reason'));
                    return;
                }
                sellerBonusMutation.mutate({
                    enabled: sellerBonusEnabled,
                    reason,
                });
            }}
        >
            <label className="block space-y-1 text-sm">
                <span className="text-xs font-semibold text-gray-700 dark:text-zinc-200">
                    {t('v2.ploomes_deals.seller_bonus.reason')}
                </span>
                <textarea
                    rows={3}
                    value={sellerBonusReason}
                    onChange={(e) => setSellerBonusReason(e.target.value)}
                    placeholder={sellerBonusEnabled
                        ? t('v2.ploomes_deals.seller_bonus.reason_ph_enable')
                        : t('v2.ploomes_deals.seller_bonus.reason_ph_disable')}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                />
            </label>
        </ConfirmModal>

        <DealCommissionEditModal
            open={editCommissionOpen}
            onClose={() => setEditCommissionOpen(false)}
            dealId={dealId}
            preview={preview}
        />

        <DealCollaboratorLinkModal
            open={collaboratorLinkOpen}
            onClose={() => setCollaboratorLinkOpen(false)}
            dealId={dealId}
        />

        <DealSdrLinkModal
            open={sdrLinkOpen}
            onClose={() => setSdrLinkOpen(false)}
            dealId={dealId}
        />

        <DealSellerLinkModal
            open={sellerLinkOpen}
            onClose={() => setSellerLinkOpen(false)}
            dealId={dealId}
        />

        <DealLegacyCascadeModal
            open={legacyCascadeOpen}
            onClose={() => setLegacyCascadeOpen(false)}
            dealId={dealId}
        />

        <V2Modal
            open={historyAttachment !== null && Boolean(historyAttachment?.attachment_url)}
            onClose={() => setHistoryAttachment(null)}
            size="2xl"
            title={historyAttachment?.attachment_name ?? t('v2.ploomes_deals.commission_edit.attachment')}
            description={historyAttachment?.changed_by?.name ?? undefined}
            footer={
                <Button type="button" variant="outline" onClick={() => setHistoryAttachment(null)}>
                    {t('v2.ploomes_deals.commission_edit.cancel')}
                </Button>
            }
        >
            {historyAttachment?.attachment_url && historyAttachment.attachment_kind && (
                <AttachmentPreview
                    kind={historyAttachment.attachment_kind}
                    url={historyAttachment.attachment_url}
                    name={historyAttachment.attachment_name ?? 'anexo'}
                    embedded
                />
            )}
        </V2Modal>

    </>
    );
}

function formatRuleDate(value?: string | null): string {
    if (!value) {
        return '—';
    }
    const d = new Date(`${value}T12:00:00`);
    if (Number.isNaN(d.getTime())) {
        return value;
    }
    return d.toLocaleDateString('pt-BR');
}

function FrozenRulesModal({
    open,
    version,
    meta,
    snapshot,
    appliedDeflators,
    appliedAccelerators,
    carenciaApplied,
    onClose,
}: {
    open: boolean;
    version: string;
    meta: CommissionPreview['rule_version_meta'];
    snapshot: CommissionPreview['rules_snapshot'];
    appliedDeflators: NonNullable<CommissionPreview['deflators']>;
    appliedAccelerators: NonNullable<CommissionPreview['accelerators']>;
    carenciaApplied: boolean;
    onClose: () => void;
}) {
    const { t } = useI18n();
    const [showOnlyApplied, setShowOnlyApplied] = useState(false);
    const carencia = snapshot?.carencia;
    const carenciaCatalogActive = carencia?.active !== false;
    const carenciaPp = ((carencia?.deflator_per_month ?? 0.002) * 100).toFixed(2);
    const appliedNames = new Set([
        ...appliedDeflators.map((d) => d.name),
        ...appliedAccelerators.map((a) => a.name),
    ]);
    const isCurrent = meta?.is_current === true;
    const periodLabel = isCurrent
        ? t('v2.ploomes_deals.rules_period_since', { from: formatRuleDate(meta?.valid_from) })
        : t('v2.ploomes_deals.rules_period_range', {
            from: formatRuleDate(meta?.valid_from),
            until: formatRuleDate(meta?.valid_until),
        });

    return (
        <V2Modal
            open={open}
            onClose={onClose}
            title={t('v2.ploomes_deals.rules_modal_title', { version })}
            description={t('v2.ploomes_deals.rules_modal_subtitle')}
            size="2xl"
            footer={(
                <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200"
                >
                    {t('v2.ploomes_deals.rules_modal_close')}
                </button>
            )}
        >
            {!snapshot ? (
                <p className="text-sm text-gray-500">{t('v2.ploomes_deals.rules_modal_empty')}</p>
            ) : (
                <div className="space-y-4 text-sm">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 dark:border-zinc-700 dark:bg-zinc-900/50">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="space-y-1">
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                    {t('v2.ploomes_deals.rules_period_label')}
                                </p>
                                <p className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
                                    {periodLabel}
                                </p>
                                {meta?.published_at && (
                                    <p className="text-xs text-gray-500">
                                        {t('v2.ploomes_deals.rules_approved_at', {
                                            date: new Date(meta.published_at).toLocaleString('pt-BR'),
                                        })}
                                    </p>
                                )}
                            </div>

                            <div className="flex items-center gap-2">
                                <span className={cn(
                                    'text-xs font-medium',
                                    !isCurrent ? 'text-amber-700 dark:text-amber-300' : 'text-gray-400',
                                )}>
                                    {t('v2.ploomes_deals.rules_status_legacy')}
                                </span>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={isCurrent}
                                    title={isCurrent
                                        ? t('v2.ploomes_deals.rules_status_current_hint')
                                        : t('v2.ploomes_deals.rules_status_legacy_hint')}
                                    className={cn(
                                        'relative inline-flex h-7 w-12 shrink-0 cursor-default items-center rounded-full transition-colors',
                                        isCurrent ? 'bg-emerald-500' : 'bg-amber-400',
                                    )}
                                >
                                    <span
                                        className={cn(
                                            'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
                                            isCurrent ? 'translate-x-6' : 'translate-x-1',
                                        )}
                                    />
                                </button>
                                <span className={cn(
                                    'text-xs font-medium',
                                    isCurrent ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-400',
                                )}>
                                    {t('v2.ploomes_deals.rules_status_current')}
                                </span>
                            </div>
                        </div>
                        <p className="mt-2 text-xs text-gray-500 dark:text-zinc-400">
                            {isCurrent
                                ? t('v2.ploomes_deals.rules_status_current_hint')
                                : t('v2.ploomes_deals.rules_status_legacy_hint')}
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-gray-500 dark:text-zinc-400">
                            {t('v2.ploomes_deals.rules_modal_highlight_hint')}
                        </p>
                        <button
                            type="button"
                            onClick={() => setShowOnlyApplied((v) => !v)}
                            className={cn(
                                'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors',
                                showOnlyApplied
                                    ? 'border-indigo-300 bg-indigo-50 text-indigo-800 dark:border-indigo-500/40 dark:bg-indigo-950/40 dark:text-indigo-300'
                                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300',
                            )}
                        >
                            {showOnlyApplied
                                ? <PiToggleRightFill className="h-4 w-4 text-indigo-600" />
                                : <PiToggleLeftBold className="h-4 w-4" />}
                            {t('v2.ploomes_deals.rules_toggle_applied_only')}
                        </button>
                    </div>

                    <div
                        className={cn(
                            'rounded-xl border px-3 py-2',
                            carenciaApplied
                                ? 'border-indigo-300 bg-indigo-50 ring-2 ring-indigo-400/40 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:ring-indigo-400/30'
                                : showOnlyApplied
                                  ? 'hidden'
                                  : 'border-gray-200 bg-gray-50 opacity-70 dark:border-zinc-700 dark:bg-zinc-900/40',
                        )}
                    >
                        <div className="flex flex-wrap items-center gap-2">
                            <p className={cn(
                                'text-xs font-semibold uppercase tracking-wide',
                                carenciaApplied
                                    ? 'text-indigo-800 dark:text-indigo-300'
                                    : 'text-gray-500 dark:text-zinc-400',
                            )}>
                                {t('v2.ploomes_deals.rules_modal_carencia')}
                            </p>
                            {carenciaApplied && (
                                <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                                    {t('v2.ploomes_deals.rules_modal_applied_badge')}
                                </span>
                            )}
                        </div>
                        <p className={cn(
                            'mt-1 text-sm',
                            carenciaApplied
                                ? 'text-indigo-950 dark:text-indigo-100'
                                : 'text-gray-600 dark:text-zinc-400',
                        )}>
                            {carenciaCatalogActive
                                ? t('v2.ploomes_deals.rules_modal_carencia_active', { pp: carenciaPp })
                                : t('v2.ploomes_deals.rules_modal_carencia_inactive')}
                        </p>
                    </div>

                    <FrozenRulesTable
                        title={t('v2.ploomes_deals.rules_modal_deflators')}
                        rows={snapshot.deflators ?? []}
                        appliedNames={appliedNames}
                        showOnlyApplied={showOnlyApplied}
                    />
                    <FrozenRulesTable
                        title={t('v2.ploomes_deals.rules_modal_accelerators')}
                        rows={snapshot.accelerators ?? []}
                        appliedNames={appliedNames}
                        showOnlyApplied={showOnlyApplied}
                    />
                </div>
            )}
        </V2Modal>
    );
}

function FrozenRulesTable({
    title,
    rows,
    appliedNames,
    showOnlyApplied,
}: {
    title: string;
    rows: NonNullable<NonNullable<CommissionPreview['rules_snapshot']>['deflators']>;
    appliedNames: Set<string>;
    showOnlyApplied: boolean;
}) {
    const { t } = useI18n();

    const sorted = [...rows]
        .filter((row) => !showOnlyApplied || appliedNames.has(row.name))
        .sort((a, b) => {
            const aOn = appliedNames.has(a.name) ? 0 : 1;
            const bOn = appliedNames.has(b.name) ? 0 : 1;
            return aOn - bOn;
        });

    if (sorted.length === 0) {
        return null;
    }

    return (
        <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-zinc-700">
                <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 text-left text-gray-500 dark:bg-zinc-800/80">
                        <tr>
                            <th className="px-3 py-2">{t('v2.ploomes_deals.rules_modal_col_name')}</th>
                            <th className="px-3 py-2">{t('v2.ploomes_deals.rules_modal_col_condition')}</th>
                            <th className="px-3 py-2">{t('v2.ploomes_deals.rules_modal_col_delta')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((row, idx) => {
                            const applied = appliedNames.has(row.name);
                            return (
                                <tr
                                    key={row.id ?? idx}
                                    className={cn(
                                        'border-t',
                                        applied
                                            ? 'border-indigo-200 bg-indigo-50/90 dark:border-indigo-800/60 dark:bg-indigo-950/50'
                                            : 'border-gray-100 opacity-55 dark:border-zinc-800',
                                    )}
                                >
                                    <td className={cn(
                                        'px-3 py-2 font-medium',
                                        applied
                                            ? 'text-indigo-950 dark:text-indigo-100'
                                            : 'text-gray-600 dark:text-zinc-400',
                                    )}>
                                        <span className="inline-flex flex-wrap items-center gap-1.5">
                                            {applied && (
                                                <span className="inline-block h-1.5 w-1.5 rounded-full bg-indigo-500" />
                                            )}
                                            {row.name}
                                            {applied && (
                                                <span className="rounded-full bg-indigo-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                                                    {t('v2.ploomes_deals.rules_modal_applied_badge')}
                                                </span>
                                            )}
                                        </span>
                                    </td>
                                    <td className={cn(
                                        'px-3 py-2',
                                        applied ? 'text-indigo-800/80 dark:text-indigo-300/80' : 'text-gray-400',
                                    )}>
                                        {row.condition_type ?? '—'}
                                    </td>
                                    <td className={cn(
                                        'px-3 py-2 tabular-nums',
                                        applied
                                            ? 'font-semibold text-indigo-900 dark:text-indigo-100'
                                            : 'text-gray-500 dark:text-zinc-500',
                                    )}>
                                        {row.delta_rate != null
                                            ? `${(row.delta_rate * 100).toFixed(2)} p.p.`
                                            : row.per_pct_above_floor != null
                                              ? `${(row.per_pct_above_floor * 100).toFixed(2)} p.p./1%`
                                              : row.multiplier != null
                                                ? `×${row.multiplier}`
                                                : '—'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ─── Modal de tabela de regras ───────────────────────────────── */

type PaymentRule = {
    id: number;
    payment_condition: string;
    payment_label: string;
    installments_max: number;
    seller_alcada: number;
    seller_base_rate: number;
    manager_alcada: number;
    manager_adjusted_rate: number;
    director_alcada: number;
    director_adjusted_rate: number;
    discount_floor: number;
    rate_per_pct_above_floor: number;
};

type Step = { low: number; high: number; rate: number; isMin: boolean };

function fmtN(v: number) {
    return v % 1 === 0 ? String(v) : v.toFixed(1).replace('.', ',');
}
function fmtPct(v: number) {
    return (v * 100).toFixed(1).replace('.', ',') + '%';
}

const NUM_MODAL_STEPS = 5;

function computeSteps(rule: PaymentRule): Step[] {
    const floor = rule.discount_floor ?? rule.manager_alcada;
    const perPct = rule.rate_per_pct_above_floor ?? 0.001;
    const numSteps = Math.round(rule.director_alcada - floor);
    const steps: Step[] = [];
    for (let i = 1; i <= numSteps; i++) {
        const rate = Math.max(rule.director_adjusted_rate, rule.seller_base_rate - i * perPct);
        const isMin = Math.abs(rate - rule.director_adjusted_rate) < 0.00001;
        steps.push({
            low: floor + (i - 1) === 0 ? 0.1 : floor + (i - 1) + 0.1,
            high: floor + i,
            rate,
            isMin,
        });
    }
    return steps;
}

/** Determina qual zona do desconto (seller, manager, stepN) o deal cai. */
function resolveZone(
    rule: PaymentRule,
    discountPct: number,
): { zone: 'seller' | 'manager' | `step_${number}` } {
    if (discountPct <= rule.seller_alcada) return { zone: 'seller' };
    if (discountPct <= rule.manager_alcada) return { zone: 'manager' };

    const floor = rule.discount_floor ?? rule.manager_alcada;
    const steps = computeSteps(rule);
    for (let i = 0; i < steps.length; i++) {
        if (discountPct <= steps[i].high || i === steps.length - 1) {
            return { zone: `step_${i}` as `step_${number}` };
        }
    }
    return { zone: `step_${steps.length - 1}` as `step_${number}` };
}

function PaymentRulesModal({
    paymentCondition,
    paymentLabel,
    paymentParcelas,
    discountPct,
    onClose,
}: {
    paymentCondition: string | null;
    paymentLabel: string | null;
    paymentParcelas: number | null;
    discountPct: number | null;
    onClose: () => void;
}) {
    const { data, isPending } = useQuery<{ data: PaymentRule[] }>({
        queryKey: ['commissioning', 'payment-rules'],
        queryFn: () => api.get('/commissioning/payment-rules').then((r) => r.data),
        staleTime: 5 * 60_000,
    });

    const rules = data?.data ?? [];

    const TH = 'bg-[#1a2744] text-white text-center text-[10px] font-semibold px-1.5 py-1.5 border border-[#263359] whitespace-nowrap dark:bg-zinc-900 dark:border-zinc-700';
    const TD = 'text-center text-xs px-1.5 py-2 border border-gray-100 whitespace-nowrap text-gray-800 dark:border-zinc-700 dark:text-zinc-200';

    const description =
        discountPct != null && (paymentCondition || paymentLabel)
            ? `Negócio: ${paymentLabel || paymentCondition} · Desconto: ${discountPct.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })}%`
            : undefined;

    return (
        <V2Modal
            open
            onClose={onClose}
            title="Tabela de Comissão por Condição de Pagamento"
            description={description}
            size="5xl"
        >
            {/* Estilos locais: garante o pulso mesmo se o Vite não recarregar o globals.css */}
            <style>{`
                @keyframes cmt-pulse-indigo {
                    0%, 100% { background-color: #4f46e5; box-shadow: 0 0 0 0 rgba(99,102,241,.35); opacity: 1; }
                    50% { background-color: #6366f1; box-shadow: 0 0 0 4px rgba(99,102,241,0); opacity: .88; }
                }
                @keyframes cmt-pulse-green {
                    0%, 100% { background-color: #16a34a; box-shadow: 0 0 0 0 rgba(34,197,94,.3); opacity: 1; }
                    50% { background-color: #22c55e; box-shadow: 0 0 0 4px rgba(34,197,94,0); opacity: .88; }
                }
                @keyframes cmt-pulse-yellow {
                    0%, 100% { background-color: #eab308; box-shadow: 0 0 0 0 rgba(234,179,8,.3); opacity: 1; }
                    50% { background-color: #facc15; box-shadow: 0 0 0 4px rgba(234,179,8,0); opacity: .88; }
                }
                .cmt-pulse-indigo {
                    display: inline-block; border-radius: .375rem; padding: .2rem .45rem;
                    color: #fff; font-weight: 700; line-height: 1.2;
                    animation: cmt-pulse-indigo 2s ease-in-out infinite;
                }
                .cmt-pulse-green {
                    display: inline-flex; border-radius: 9999px; padding: .2rem .45rem;
                    color: #fff; font-weight: 600; font-size: 10px; line-height: 1.2;
                    animation: cmt-pulse-green 2s ease-in-out infinite;
                }
                .cmt-pulse-yellow {
                    display: inline-flex; border-radius: 9999px; padding: .2rem .45rem;
                    color: #713f12; font-weight: 600; font-size: 10px; line-height: 1.2;
                    animation: cmt-pulse-yellow 2s ease-in-out infinite;
                }
            `}</style>
            <div className="-mx-5 -mt-4 mb-4 flex flex-wrap gap-3 border-b border-gray-100 bg-gray-50 px-5 py-3 text-xs text-gray-600 dark:border-zinc-800 dark:bg-zinc-800/60 dark:text-zinc-300">
                <span className="flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-sm border border-green-400 bg-green-200" />
                    Zona Vendedor
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-sm border border-yellow-400 bg-yellow-200" />
                    Zona Gerência
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-sm bg-red-600" />
                    Zona Diretoria (escalonado)
                </span>
                {(paymentCondition || paymentLabel) && discountPct != null && (
                    <span className="ml-auto flex items-center gap-1.5 font-semibold text-indigo-700 dark:text-indigo-300">
                        <span className="inline-block h-3 w-3 rounded-sm bg-indigo-500 ring-2 ring-indigo-300 dark:ring-indigo-500/50" />
                        ★ Faixa deste negócio
                    </span>
                )}
            </div>

            {isPending && (
                <LoadingState variant="inline" className="py-6" />
            )}

            {!isPending && rules.length > 0 && (
                <div className="overflow-x-auto rounded-lg dark:bg-zinc-950/40">
                    <table className="w-full border-collapse text-xs">
                        <thead>
                            <tr>
                                <th className={`${TH} pl-3 text-left`} rowSpan={2}>Condição</th>
                                <th className={TH} rowSpan={2}><div>Teto Desc.</div><div className="font-normal opacity-60">Vendedor</div></th>
                                <th className={`${TH} border-yellow-500 bg-yellow-600 dark:border-yellow-700 dark:bg-yellow-700`} rowSpan={2}><div>Teto Desc.</div><div className="font-normal opacity-70">Gerência</div></th>
                                <th className={`${TH} border-[#175530] bg-[#1e6b38] dark:border-emerald-900 dark:bg-emerald-900`} rowSpan={2}><div>Comissão Base</div><div className="font-normal opacity-60">0% ao teto</div></th>
                                {Array.from({ length: NUM_MODAL_STEPS }).map((_, i) => (
                                    <th key={i} colSpan={2} className={`${TH} border-[#6b1212] bg-[#8b1a1a] dark:border-red-950 dark:bg-red-950`}>
                                        Step {i + 1}
                                    </th>
                                ))}
                            </tr>
                            <tr>
                                {Array.from({ length: NUM_MODAL_STEPS }).map((_, i) => (
                                    <React.Fragment key={i}>
                                        <th className={`${TH} bg-[#7a1616] py-1 text-[9px] dark:bg-red-950/90`}>Faixa</th>
                                        <th className={`${TH} bg-[#9b1e1e] py-1 text-[9px] dark:bg-red-900/80`}>Taxa</th>
                                    </React.Fragment>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-zinc-700">
                            {rules.map((rule) => {
                                const normalize = (s: string) => s.toLowerCase().replace(/[\s\/]/g, '');
                                // Só usa fallback por parcelas se nenhuma linha já bateu por chave ou label
                                const hasKeyOrLabelMatch = rules.some(
                                    (r) =>
                                        (paymentCondition != null && r.payment_condition === paymentCondition) ||
                                        (paymentLabel != null && normalize(r.payment_label) === normalize(paymentLabel)),
                                );
                                const matchByKey = paymentCondition != null && rule.payment_condition === paymentCondition;
                                const matchByLabel = paymentLabel != null && normalize(rule.payment_label) === normalize(paymentLabel);
                                // Parcelas: menor regra cujo installments_max comporta o valor
                                const matchByParcelas =
                                    !hasKeyOrLabelMatch &&
                                    paymentParcelas != null &&
                                    paymentParcelas <= rule.installments_max &&
                                    !rules.some(
                                        (r) =>
                                            r !== rule &&
                                            r.installments_max < rule.installments_max &&
                                            r.installments_max >= paymentParcelas,
                                    );
                                const isMatchRow = matchByKey || matchByLabel || matchByParcelas;
                                const zone = isMatchRow && discountPct != null
                                    ? resolveZone(rule, discountPct)
                                    : null;
                                const steps = computeSteps(rule);
                                const padded: (Step | null)[] = [
                                    ...steps,
                                    ...Array(Math.max(0, NUM_MODAL_STEPS - steps.length)).fill(null),
                                ];

                                const rowBg = isMatchRow
                                    ? 'bg-indigo-50 ring-1 ring-inset ring-indigo-400 dark:bg-indigo-950/45 dark:ring-indigo-500'
                                    : 'hover:bg-gray-50/60 dark:hover:bg-zinc-800/50';

                                function highlight(z: string) {
                                    return isMatchRow && zone?.zone === z
                                        ? 'ring-2 ring-inset ring-indigo-500 bg-indigo-200 font-bold text-indigo-900 dark:bg-indigo-800/70 dark:text-indigo-100 dark:ring-indigo-400'
                                        : '';
                                }

                                return (
                                    <tr key={rule.payment_condition} className={`transition-colors ${rowBg}`}>
                                        <td className={`${TD} pl-3 text-left font-semibold`}>
                                            <div className="flex items-center gap-1.5">
                                                {isMatchRow && (
                                                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-indigo-500" />
                                                )}
                                                {rule.payment_label}
                                            </div>
                                        </td>

                                        <td className={`${TD} ${highlight('seller')}`}>
                                            {isMatchRow && zone?.zone === 'seller' ? (
                                                <span className="cmt-pulse-green">até {rule.seller_alcada}%</span>
                                            ) : (
                                                <span className="inline-flex rounded-full border border-green-200 bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold text-green-800 dark:border-green-800/60 dark:bg-green-950/60 dark:text-green-300">
                                                    até {rule.seller_alcada}%
                                                </span>
                                            )}
                                        </td>

                                        <td className={`${TD} bg-yellow-50 dark:bg-yellow-950/35 ${highlight('manager')}`}>
                                            {isMatchRow && (zone?.zone === 'manager' || zone?.zone?.startsWith('step_')) ? (
                                                <span className="cmt-pulse-yellow">até {rule.manager_alcada}%</span>
                                            ) : (
                                                <span className="inline-flex rounded-full border border-yellow-300 bg-yellow-200 px-1.5 py-0.5 text-[10px] font-semibold text-yellow-900 dark:border-yellow-700/60 dark:bg-yellow-900/50 dark:text-yellow-200">
                                                    até {rule.manager_alcada}%
                                                </span>
                                            )}
                                        </td>

                                        {(() => {
                                            const inBase = isMatchRow && (zone?.zone === 'seller' || zone?.zone === 'manager');
                                            return (
                                                <td className={`${TD} ${inBase ? 'bg-indigo-50 ring-2 ring-inset ring-indigo-500 dark:bg-indigo-950/50 dark:ring-indigo-400' : 'bg-green-50 dark:bg-emerald-950/40'}`}>
                                                    <div className={`font-bold ${inBase ? 'text-indigo-800 dark:text-indigo-200' : 'text-green-800 dark:text-emerald-300'}`}>{fmtPct(rule.seller_base_rate)}</div>
                                                    <div className={`text-[9px] ${inBase ? 'font-semibold text-indigo-600 dark:text-indigo-300' : 'text-green-600 dark:text-emerald-400/90'}`}>0% a {fmtN(rule.manager_alcada)}%</div>
                                                </td>
                                            );
                                        })()}

                                        {padded.map((step, i) => {
                                            const isSelectedRate = zone?.zone === `step_${i}` && isMatchRow;

                                            return !step ? (
                                                <React.Fragment key={i}>
                                                    <td className={`${TD} bg-red-50 text-gray-300 dark:bg-red-950/30 dark:text-zinc-600`} colSpan={2}>—</td>
                                                </React.Fragment>
                                            ) : (
                                                <React.Fragment key={i}>
                                                    <td className={`${TD} bg-red-50 text-[10px] text-red-700 dark:bg-red-950/35 dark:text-red-300 ${highlight(`step_${i}`)}`}>
                                                        {fmtN(step.low)}% a {fmtN(step.high)}%
                                                    </td>
                                                    <td className={`${TD} font-bold text-white ${
                                                        isSelectedRate
                                                            ? 'bg-indigo-700 dark:bg-indigo-600'
                                                            : step.isMin
                                                              ? 'bg-red-800 dark:bg-red-900'
                                                              : 'bg-red-600 dark:bg-red-800'
                                                    }`}>
                                                        {isSelectedRate ? (
                                                            <span className="cmt-pulse-indigo">{fmtPct(step.rate)}</span>
                                                        ) : (
                                                            <>
                                                                {fmtPct(step.rate)}
                                                                {step.isMin && (
                                                                    <div className="text-[8px] font-normal opacity-70">mín</div>
                                                                )}
                                                            </>
                                                        )}
                                                    </td>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <p className="mt-4 text-[10px] text-gray-400 dark:text-zinc-500">
                ★ = taxa mínima (teto Diretoria). A taxa cai 0,1 p.p. por 1% de desconto acima do teto de gerência.
            </p>
        </V2Modal>
    );
}

function formatRatePp(value: number | null | undefined): string {
    if (value == null) {
        return '—';
    }
    return `${value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%`;
}

function resolveSellerCommissionRatePp(
    preview: CommissionPreview,
    includeBonus = true,
): number | null {
    if (preview.commission_floor && preview.minimum_rate_pp != null) {
        return preview.minimum_rate_pp;
    }

    // Taxa contratual explícita (legado congelado / exceção / Buckler One).
    // NÃO usar final÷venda: em cronograma parcial, rateio ou entrada errada isso
    // inventa % (ex.: #995 → 1,56% em vez de 2,4%).
    if (preview.rate_with_exceptions_pp != null) {
        return preview.rate_with_exceptions_pp;
    }
    if (preview.fixed_rate_pp != null) {
        return preview.fixed_rate_pp;
    }
    if (preview.result_rate_pp != null) {
        return preview.result_rate_pp;
    }

    const base = preview.base_rate_pp ?? preview.base_detail?.final_rate_pp ?? 0;
    const deflator = Math.abs(preview.deflator_pp ?? 0);
    const accelerator = preview.accelerator_pp ?? 0;
    const bonus = includeBonus ? (preview.bonus_rate_pp ?? 0) : 0;
    const composed = Math.max(0, base - deflator + accelerator + bonus);

    if (base > 0 || accelerator > 0 || bonus > 0 || deflator > 0) {
        return composed;
    }

    const paid = preview.final_commission ?? preview.paid_commission_amount;
    if (paid != null && preview.deal_amount != null && preview.deal_amount > 0) {
        return (paid / preview.deal_amount) * 100;
    }

    return composed;
}

function SellerCommissionSummary({
    preview,
    rateLabel = 'Porcentagem do vendedor',
}: {
    preview: CommissionPreview;
    rateLabel?: string;
}) {
    const netAmount = preview.final_commission ?? preview.paid_commission_amount ?? 0;
    const grossAmount = preview.gross_commission ?? netAmount;
    const deductions = preview.deductions ?? [];
    const hasDeductions = deductions.length > 0 && grossAmount > netAmount + 0.009;
    const amountLabel = formatBrl(netAmount);
    const ratePp = resolveSellerCommissionRatePp(preview);
    const hadFloor = Boolean(preview.commission_floor);
    const originalRatePp =
        hadFloor && preview.original_commission_rate != null
            ? preview.original_commission_rate * 100
            : null;
    const originalAmount =
        hadFloor && preview.original_commission_amount != null
            ? preview.original_commission_amount
            : null;
    const originalAmountLabel = originalAmount != null ? formatBrl(originalAmount) : null;
    const dealAmountLabel = preview.deal_amount != null ? formatBrl(preview.deal_amount) : null;

    return (
        <div className="flex min-w-0 w-full flex-col gap-3">
            {hasDeductions && (
                <div className="rounded-2xl border border-sky-200 bg-sky-50/80 px-4 py-3 dark:border-sky-900/50 dark:bg-sky-950/30">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-sky-800 dark:text-sky-300">
                        Composição do valor líquido
                    </p>
                    <div className="mt-2 space-y-1.5 text-sm">
                        <div className="flex items-baseline justify-between gap-3">
                            <span className="text-sky-900/80 dark:text-sky-200/80">Comissão calculada</span>
                            <span className="font-semibold tabular-nums text-sky-950 dark:text-sky-100">
                                {formatBrl(grossAmount)}
                            </span>
                        </div>
                        {deductions.map((d) => (
                            <div
                                key={`${d.type}-${d.label}`}
                                className="flex items-baseline justify-between gap-3 text-rose-700 dark:text-rose-300"
                            >
                                <span className="min-w-0 truncate">
                                    − {d.type === 'sdr' ? 'SDR' : d.type === 'split' ? 'Rateio' : 'Abatimento'}
                                    {': '}
                                    <span className="font-medium">{d.label.replace(/^SDR\s+/i, '')}</span>
                                </span>
                                <span className="shrink-0 font-semibold tabular-nums">
                                    − {formatBrl(d.amount)}
                                </span>
                            </div>
                        ))}
                        <div className="flex items-baseline justify-between gap-3 border-t border-sky-200/80 pt-1.5 dark:border-sky-800/60">
                            <span className="font-semibold text-emerald-800 dark:text-emerald-300">Valor líquido do vendedor</span>
                            <span className="font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
                                {formatBrl(netAmount)}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex min-w-0 w-full flex-col gap-3 lg:flex-row lg:items-stretch">
            <div
                className={cn(
                    '@container flex min-w-0 flex-1 flex-col justify-center overflow-hidden rounded-2xl border px-4 py-4 sm:px-5 sm:py-5',
                    'border-emerald-200/80 bg-emerald-50',
                    'dark:border-emerald-500/30 dark:bg-emerald-950/50',
                )}
            >
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-400/90">
                    Comissão líquida do vendedor
                </p>
                {hasDeductions && (
                    <p className="mt-1 text-[11px] text-emerald-800/70 dark:text-emerald-400/70">
                        Valor líquido após {deductions.map((d) => (d.type === 'sdr' ? 'SDR' : 'rateio')).join(' e ')}
                    </p>
                )}
                {originalAmountLabel != null && (
                    <p className="mt-1.5 max-w-full break-words text-sm font-medium leading-tight tabular-nums text-emerald-800/60 line-through dark:text-emerald-500/50">
                        {originalAmountLabel}
                    </p>
                )}
                {!hasDeductions && grossAmount > netAmount + 0.009 && (
                    <p className="mt-1.5 max-w-full break-words text-sm font-medium leading-tight tabular-nums text-emerald-800/60 line-through dark:text-emerald-500/50">
                        {formatBrl(grossAmount)}
                    </p>
                )}
                <p
                    className={cn(
                        'max-w-full break-words font-bold leading-tight tabular-nums tracking-tight text-emerald-700 dark:text-emerald-300',
                        'text-[clamp(1rem,7cqw,1.5rem)]',
                        originalAmountLabel != null || hasDeductions ? 'mt-0.5' : 'mt-1.5',
                    )}
                >
                    {amountLabel}
                </p>
                {hadFloor && (
                    <p className="mt-1.5 text-xs font-medium leading-snug text-emerald-800/90 dark:text-emerald-400/80">
                        Arredondada para {preview.minimum_rate_pp ?? 1}% mínimo desta venda
                    </p>
                )}
                {dealAmountLabel != null && (
                    <p className="mt-1.5 max-w-full break-words text-[11px] leading-snug text-emerald-700/70 dark:text-emerald-500/70">
                        Sobre venda de {dealAmountLabel}
                        {preview.discount_pct != null && preview.discount_pct > 0 && (
                            <>
                                {' '}
                                · desconto{' '}
                                <span className="font-semibold text-emerald-800 dark:text-emerald-300">
                                    {preview.discount_pct.toLocaleString('pt-BR', {
                                        minimumFractionDigits: 1,
                                        maximumFractionDigits: 2,
                                    })}
                                    %
                                </span>
                            </>
                        )}
                    </p>
                )}
            </div>

            <div
                className={cn(
                    'flex w-full shrink-0 flex-col items-center justify-center rounded-2xl border px-4 py-3.5 text-center',
                    'border-emerald-300 bg-white dark:border-emerald-800/60 dark:bg-gray-900',
                    'lg:w-auto lg:min-w-[7.5rem] lg:max-w-[9rem]',
                )}
            >
                <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                    {rateLabel}
                </p>
                {originalRatePp != null && (
                    <p className="mt-1 text-xs font-semibold tabular-nums text-gray-400 line-through dark:text-zinc-500">
                        {formatRatePp(originalRatePp)}
                    </p>
                )}
                <p
                    className={cn(
                        'text-xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400',
                        originalRatePp != null ? 'mt-0.5' : 'mt-1',
                    )}
                >
                    {formatRatePp(ratePp)}
                </p>
                <p className="mt-1 text-[10px] font-medium leading-snug text-emerald-700/80 dark:text-emerald-500/80">
                    {hadFloor && originalRatePp != null ? 'após arredondamento' : 'desta venda'}
                </p>
            </div>
            </div>
        </div>
    );
}

function PloomesExceptionNotice({ ratePp }: { ratePp: number | null }) {
    const rateLabel = formatRatePp(ratePp);
    // Detalhe operacional do Hub (e 1,5%): só admin / super_admin.
    const showHubExceptionDetails = isSuperAdmin() || hasRole('admin');

    if (!showHubExceptionDetails) {
        // Compacto: chip único (o título do card já leva o badge «Exceção»).
        return (
            <div
                className="inline-flex max-w-full items-center gap-2 rounded-full border border-amber-300 bg-amber-50 py-1.5 pe-3 ps-1.5 dark:border-amber-800/60 dark:bg-amber-950/40"
                role="status"
            >
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white">
                    <PiWarningBold className="size-3.5" aria-hidden />
                </span>
                <span className="text-xs font-medium text-amber-900 dark:text-amber-100">
                    Taxa fixa Ploomes
                </span>
                <span className="rounded-full bg-amber-500 px-2.5 py-0.5 text-sm font-bold tabular-nums text-white">
                    {rateLabel}
                </span>
            </div>
        );
    }

    return (
        <div
            className={cn(
                'flex flex-col gap-3 sm:flex-row sm:items-stretch',
            )}
            role="status"
        >
            <div
                className={cn(
                    'flex min-w-0 flex-1 items-start gap-4 rounded-2xl border p-4',
                    'border-amber-300 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-950/30',
                )}
            >
                <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-amber-500 text-white">
                    <PiWarningBold className="size-6" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge color="warning" variant="flat" rounded="pill" size="sm">
                            Venda exceção Ploomes
                        </Badge>
                    </div>
                    <Title as="h3" className="mt-1.5 text-sm font-bold text-amber-950 dark:text-amber-100">
                        Regras automáticas do Hub desativadas
                    </Title>
                    <Text className="mt-1 text-xs leading-relaxed text-amber-800 dark:text-amber-200/90">
                        Ignora tabela, deflatores e rateio do Hub — usa o % fixo do CRM.
                        {' '}
                        O adicional de 1,5% entra se «Aplica 1,5%?» = Sim (ou ausente).
                    </Text>
                </div>
            </div>

            <div
                className={cn(
                    'flex shrink-0 flex-col items-center justify-center rounded-2xl border px-6 py-4 text-center',
                    'border-amber-300 bg-white dark:border-amber-800/60 dark:bg-gray-900',
                    'sm:min-w-[8.5rem]',
                )}
            >
                <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                    Taxa Ploomes
                </p>
                <p className="mt-1 text-3xl font-bold tabular-nums text-amber-600 dark:text-amber-400">
                    {rateLabel}
                </p>
            </div>
        </div>
    );
}

function RateChip({
    label,
    value,
    tone,
    prefix,
    icon,
}: {
    label: string;
    value: number | null | undefined;
    tone: 'neutral' | 'positive' | 'negative';
    prefix?: string;
    icon?: React.ReactNode;
}) {
    const num = value ?? 0;
    const display =
        tone === 'neutral'
            ? `${num.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%`
            : formatPp(prefix === '−' ? -num : num);

    const toneClass =
        tone === 'negative'
            ? 'border-rose-100 bg-rose-50/80 text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/40 dark:text-rose-300'
            : tone === 'positive'
              ? 'border-emerald-100 bg-emerald-50/80 text-emerald-800 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-300'
              : 'border-gray-100 bg-gray-50/80 text-gray-800 dark:border-zinc-700 dark:bg-zinc-800/70 dark:text-zinc-100';

    return (
        <div className={`rounded-lg border px-2.5 py-2 ${toneClass}`}>
            <p className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide opacity-70">
                {icon}
                {label}
            </p>
            <p className="text-sm font-bold">{display}</p>
        </div>
    );
}

function ModifierRow({
    item,
    tone,
    label,
}: {
    item: { name: string; delta_pp: number; amount_brl: number };
    tone: 'positive' | 'negative';
    label?: string;
}) {
    const displayName = label ? `${label} — ${item.name}` : item.name;
    return (
        <div className="flex items-start justify-between gap-2 text-xs">
            <span className="min-w-0 flex-1 text-gray-600 dark:text-zinc-400">{displayName}</span>
            <span className={`shrink-0 font-semibold ${tone === 'negative' ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                {formatPp(tone === 'negative' ? -Math.abs(item.delta_pp) : item.delta_pp)} · {formatBrl(item.amount_brl)}
            </span>
        </div>
    );
}

function BaseDetailRow({ detail }: {
    detail: NonNullable<import('../types').CommissionPreview['base_detail']>;
}) {
    const fmtPct = (v: number) =>
        v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

    const exceeded = detail.discount_pct > detail.seller_teto_pct;

    return (
        <div className="flex items-start justify-between gap-2 text-xs">
            <span className="min-w-0 flex-1 text-gray-600 dark:text-zinc-400">
                Base — {detail.label} · desconto {fmtPct(detail.discount_pct)}
                {exceeded && (
                    <> · alçada <span className="font-semibold text-amber-700 dark:text-amber-300">{detail.alcada_label}</span> (teto vendedor {fmtPct(detail.seller_teto_pct)})</>
                )}
            </span>
            <span className="shrink-0 font-semibold text-gray-700 dark:text-zinc-200">
                {fmtPct(detail.flat_rate_pp)}
            </span>
        </div>
    );
}
